// qs-NavigationPanel.vue
<template>
  <div class="flex gap-0.5 items-center p-1.5 bg-white/95 border-0 rounded w-auto min-w-fit h-auto min-h-11 overflow-hidden">
    <button
      v-for="button in navButtons"
      :key="button.id"
      :class="[
        'w-7 h-8 min-w-7 min-h-8 max-w-7 max-h-8 flex items-center justify-center border-0 rounded cursor-pointer transition-all duration-150 ease-in-out select-none outline-none box-border',
        getButtonClasses(button)
      ]"
      :disabled="button.disabled"
      :title="button.title"
      :data-button-id="button.id"
      @click="button.action"
    >
      <component
        :is="button.icon"
        :class="getIconClasses(button)"
      />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Component } from 'vue'
import { Activity, ChevronLeft, ChevronRight, Move, RotateCcw, Trash2 } from 'lucide-vue-next'
import { useModeManager } from '@/composables/qs-useModeManager'
import { useHistory } from '@/composables/qs-useHistory'
import { useCanvasStore } from '@/stores/qs-store-canvas'
import { usePanelStore } from '@/stores/qs-store-panels'

interface Props {}

defineProps<Props>()

interface Emits {
  (e: 'delete-mode-changed', enabled: boolean): void
  (e: 'performance-toggle'): void
}

const emit = defineEmits<Emits>()

interface NavButton {
  id: string
  icon: Component
  title: string
  action: () => void
  disabled: boolean
  active?: boolean
}

const canvasStore = useCanvasStore()
const panelStore = usePanelStore()
const modeManager = useModeManager()
const history = useHistory()

const isDeleteMode = modeManager.isDeleteMode
const isPanMode = modeManager.isPanMode
const isPerformanceVisible = computed(() => panelStore.panels.performance?.visible || false)

const navButtons = computed<NavButton[]>(() => [
  {
    id: 'back',
    icon: ChevronLeft,
    title: 'Back (Ctrl+Z)',
    action: () => history.performUndo(),
    disabled: !history.canUndo.value
  },
  {
    id: 'forward',
    icon: ChevronRight,
    title: 'Forward (Ctrl+Y)',
    action: () => history.performRedo(),
    disabled: !history.canRedo.value
  },
  {
    id: 'pan',
    icon: Move,
    title: 'Pan (M) - Move canvas',
    action: () => {
      if (isPanMode.value) {
        modeManager.switchToDrawingMode()
      } else {
        modeManager.activatePanMode()
      }
    },
    disabled: false,
    active: isPanMode.value
  },
  {
    id: 'delete',
    icon: Trash2,
    title: 'Delete Mode (D) - Select paths to delete',
    action: () => {
      if (isDeleteMode.value) {
        modeManager.switchToDrawingMode()
      } else {
        modeManager.activateDeleteMode()
      }
    },
    disabled: false,
    active: isDeleteMode.value
  },
  {
    id: 'reset',
    icon: RotateCcw,
    title: 'Reset Workspace (P)',
    action: () => {
      canvasStore.centerArtboard()
      panelStore.resetPanelPositions()
    },
    disabled: false
  },
  {
    id: 'performance',
    icon: Activity,
    title: 'Performance Monitor (K) - Performance monitoring',
    action: () => {
      panelStore.togglePanel('performance')
      emit('performance-toggle')
    },
    disabled: false,
    active: isPerformanceVisible.value
  }
])

const getButtonClasses = (button: NavButton): string => {
  if (button.disabled) {
    return 'bg-creme-lighter text-text-lighter cursor-not-allowed opacity-60'
  }
  if (button.active) {
    return button.id === 'delete'
      ? 'bg-danger text-white hover:bg-danger-dark active:bg-danger-dark'
      : 'bg-primary-500 text-creme-light hover:bg-primary-600 active:bg-primary-700'
  }
  return 'bg-white text-text hover:bg-creme-hoverbutton hover:text-text active:bg-creme-dark active:translate-y-px'
}

const getIconClasses = (button: NavButton): string => {
  const baseClasses = 'nav-icon flex-shrink-0 w-3.5 h-3.5 block mx-auto'
  if (button.disabled) {
    return `${baseClasses} nav-icon-disabled`
  }
  if (button.active) {
    return button.id === 'delete'
      ? `${baseClasses} nav-icon-active-delete`
      : `${baseClasses} nav-icon-active`
  }
  return `${baseClasses} nav-icon-default`
}
</script>

<style scoped>
.nav-icon {
  stroke-width: 1.5;
  stroke: currentColor;
  fill: none;
}

.nav-icon-default {
  color: theme('colors.text.DEFAULT');
}

.nav-icon-disabled {
  color: theme('colors.text.lighter');
}

.nav-icon-active {
  color: theme('colors.creme.light');
}

.nav-icon-active-delete {
  color: white;
}
</style>